import styles from './BlogArticle.module.scss'
import dayjs from 'dayjs'
import Head from 'next/head'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useTranslation } from 'next-i18next/pages'
import { generateArticleStructuredData } from 'utils/structuredData'
import { ArticleBody, ArticleFooter, ArticleHeader, ArticleIntro, ArticleValve } from 'components/article'
import ContentBlocks from 'components/article/content-blocks/ContentBlocks'
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs'
import ArticleSection from 'components/article/article-section/ArticleSection'
import { BlogArticleProps } from 'features/blog/article/types'
import { mapAndSortCmsBlogArticlesToArticles } from 'utils/cms'
import { Img } from '@boltenergy-be/design-system'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'

const BlogArticle = ({ article, otherArticles }: BlogArticleProps) => {
  // i18n
  const { t } = useTranslation(['blog', 'common'])

  return (
    <main className={styles['blog-article']}>
      {/* SEO */}
      <Head>
        <script
          type="application/ld+json"
          key="article-jsonld"
          dangerouslySetInnerHTML={generateArticleStructuredData({
            title: article.title,
            authorName: article.author?.firstName,
            datePublished: article.customPublishedAt ?? article.publishedAt,
            dateUpdated: article.updatedAt,
            thumbnail: article.thumbnail?.url,
            type: 'NewsArticle'
          })}
        />
      </Head>

      {/* BREADCRUMBS */}
      <nav className={classNames('container', styles['blog-nav'])}>
        <Breadcrumbs className={styles.breadcrumbs} title={article.title} />
      </nav>

      {/* HERO */}
      <section className={classNames('container', styles.hero)}>
        <Img
          publicId={article.heroImage?.provider_metadata.public_id || article.thumbnail.provider_metadata.public_id}
          priority
          fill
          sizes="100vw"
          alt=""
        />
      </section>

      {/* CONTENT */}
      <section className={classNames('container', styles.article)}>
        {/* HEADER */}
        <ArticleHeader
          author={{
            date: dayjs(article.customPublishedAt ?? article.publishedAt).format('DD/MM/YYYY'),
            imagePublicId: article.author?.avatar?.provider_metadata?.public_id,
            name: article.author?.firstName
          }}
          tag={article.category?.title}
          title={article.title}
        />

        {/* INTRO */}
        <ArticleIntro>{article.introText ? <BlocksRenderer content={article.introText} /> : parse(article.description)}</ArticleIntro>

        {/* BODY */}
        <ArticleBody>
          <ContentBlocks contentBlocks={article.contentBlocks ?? []} />
        </ArticleBody>

        {/* VALVE (afsluiter) */}
        <ArticleValve>
          {article.footer ? (
            <BlocksRenderer content={article.footer} />
          ) : article.closingText ? (
            parse(article.closingText)
          ) : (
            t(
              'articleValve',
              'Bolt helpt jou wegwijs maken in energie. Inzicht in je energie-aansluiting en energie algemeen helpen jou om de juiste keuzes te maken richting een klimaatneutrale toekomst.'
            )
          )}
        </ArticleValve>

        {/* FOOTER */}
        <ArticleFooter />
      </section>

      {/* MORE ARTICLES */}
      <ArticleSection
        title={t('common:readMore', 'Meer lezen?')}
        articles={mapAndSortCmsBlogArticlesToArticles(otherArticles)}
        className={styles['more-articles']}
      />
    </main>
  )
}

export default BlogArticle
